import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select } from '@/shared/ui/select';
import { Badge } from '@/shared/ui/badge';
import { ErrorState } from '@/shared/feedback';
import { TenantInvitationApi } from '@/modules/tenants/services/invitation-api';
import type {
  CreateInvitationRequest,
  Invitation,
  InvitationCreated,
} from '@/modules/tenants/services/invitation-api';
import { INVITATION_MAX_DAYS } from '@/api/schemas/tenant-invitations';
import { FiUserPlus, FiTrash2, FiCopy, FiCheck } from 'react-icons/fi';

const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner — full control of the tenant' },
  { value: 'staff', label: 'Staff — day-to-day operations' },
  { value: 'viewer', label: 'Viewer — read-only access' },
];

const STATUS_TONE: Record<Invitation['status'], 'positive' | 'caution' | 'critical' | 'neutral'> = {
  pending: 'caution',
  accepted: 'positive',
  revoked: 'neutral',
  expired: 'critical',
};

/**
 * Administrator invitations for a named tenant.
 *
 * The invitation is the path for assigning an administrator BEFORE that person
 * has an account: it confers a role on an email and becomes a membership when
 * the invitee accepts. The raw token is returned EXACTLY ONCE on creation and is
 * surfaced here for the operator to copy — it is never persisted or re-fetched.
 */
export function InvitationsPanel({ tenantId }: { tenantId: string }): ReactNode {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [created, setCreated] = useState<InvitationCreated | null>(null);

  const load = useCallback(async () => {
    try {
      setInvitations(await TenantInvitationApi.list(tenantId));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load invitations'));
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    TenantInvitationApi.list(tenantId)
      .then((result) => {
        setInvitations(result);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error('Failed to load invitations'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [tenantId]);

  const handleRevoke = async (invitationId: string) => {
    setError(null);
    try {
      await TenantInvitationApi.revoke(tenantId, invitationId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to revoke invitation'));
    }
  };

  return (
    <div className="space-y-6">
      <InviteForm
        tenantId={tenantId}
        onCreated={(invitation) => {
          setCreated(invitation);
          void load();
        }}
      />

      {created ? <TokenReveal invitation={created} onDismiss={() => setCreated(null)} /> : null}

      {error ? <ErrorState message={error.message} onRetry={() => void load()} /> : null}

      <InvitationList
        invitations={invitations}
        isLoading={isLoading}
        onRevoke={(id) => void handleRevoke(id)}
      />
    </div>
  );
}

function InvitationList({
  invitations,
  isLoading,
  onRevoke,
}: {
  invitations: Invitation[];
  isLoading: boolean;
  onRevoke: (invitationId: string) => void;
}): ReactNode {
  return (
    <div>
      <h4 className="mb-3 font-medium">Invitations</h4>
      {isLoading ? (
        <p className="text-sm text-content-muted">Loading…</p>
      ) : invitations.length === 0 ? (
        <p className="text-sm text-content-muted">No invitations yet.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {invitations.map((invitation) => (
            <li key={invitation.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm text-content">{invitation.email}</p>
                <p className="text-xs text-content-muted">
                  {invitation.role} · expires {new Date(invitation.expiresAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge tone={STATUS_TONE[invitation.status]}>{invitation.status}</Badge>
                {invitation.status === 'pending' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRevoke(invitation.id)}
                    aria-label={`Revoke invitation for ${invitation.email}`}
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InviteForm({
  tenantId,
  onCreated,
}: {
  tenantId: string;
  onCreated: (invitation: InvitationCreated) => void;
}): ReactNode {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<CreateInvitationRequest['role']>('staff');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const input: CreateInvitationRequest = { email: email.trim(), role };
      const days = Number.parseInt(expiresInDays, 10);
      if (Number.isFinite(days) && days > 0) input.expiresInDays = days;
      const invitation = await TenantInvitationApi.create(tenantId, input);
      setEmail('');
      setExpiresInDays('');
      onCreated(invitation);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to create invitation'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <h4 className="font-medium">Invite an administrator</h4>
      <InviteFields
        email={email}
        role={role}
        expiresInDays={expiresInDays}
        onEmail={setEmail}
        onRole={setRole}
        onExpiresInDays={setExpiresInDays}
      />
      {error ? <ErrorState message={error.message} /> : null}
      <Button
        onClick={() => void handleSubmit()}
        loading={isSubmitting}
        disabled={email.trim() === ''}
      >
        <FiUserPlus className="mr-2 h-4 w-4" />
        Send invitation
      </Button>
    </div>
  );
}

function InviteFields({
  email,
  role,
  expiresInDays,
  onEmail,
  onRole,
  onExpiresInDays,
}: {
  email: string;
  role: CreateInvitationRequest['role'];
  expiresInDays: string;
  onEmail: (value: string) => void;
  onRole: (value: CreateInvitationRequest['role']) => void;
  onExpiresInDays: (value: string) => void;
}): ReactNode {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(event) => onEmail(event.currentTarget.value)}
        hint="The invitee’s email address."
      />
      <Select
        label="Role"
        options={ROLE_OPTIONS}
        value={role}
        onChange={(event) => onRole(event.target.value as CreateInvitationRequest['role'])}
      />
      <Input
        label="Expires in (days)"
        type="number"
        min={1}
        max={INVITATION_MAX_DAYS}
        value={expiresInDays}
        onChange={(event) => onExpiresInDays(event.currentTarget.value)}
        hint={`Optional. Defaults to the server policy. Max ${INVITATION_MAX_DAYS}.`}
      />
    </div>
  );
}

/**
 * The one-time token reveal.
 *
 * The token is returned exactly once and cannot be re-fetched. This panel makes
 * that explicit so an operator does not navigate away assuming they can find it
 * again.
 */
function TokenReveal({
  invitation,
  onDismiss,
}: {
  invitation: InvitationCreated;
  onDismiss: () => void;
}): ReactNode {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!invitation.token) return;
    try {
      await navigator.clipboard.writeText(invitation.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-positive bg-positive-soft p-4">
      <p className="text-sm font-medium text-positive">
        Invitation created for {invitation.email}
      </p>
      {invitation.token ? (
        <>
          <p className="text-sm text-content">
            Copy this token now — it is shown <strong>only once</strong> and cannot be retrieved
            again.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-surface-raised px-3 py-2 font-mono text-xs text-content">
              {invitation.token}
            </code>
            <Button variant="secondary" size="sm" onClick={() => void copy()}>
              {copied ? <FiCheck className="h-4 w-4" /> : <FiCopy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </>
      ) : (
        <p className="text-sm text-content">
          This was an idempotent replay — no new token is issued.
        </p>
      )}
      <Button variant="ghost" size="sm" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}
